import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import "./SolutionLink.css";

export type SolutionLinkVariant = "ai" | "ecommerce" | "software";

interface SolutionLinkProps {
  /** Landing de la solucion (la money page de esa linea de negocio). */
  to: string;
  label: string;
  /** Etiqueta chica arriba del texto: avisa que es una pagina propia, no un ancla. */
  kicker: string;
  /** Color y contraste de la tarjeta segun el fondo de su seccion. */
  variant: SolutionLinkVariant;
}

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const ICONS: Record<SolutionLinkVariant, ReactNode> = {
  ai: (
    <>
      <path d="M11 3l1.8 4.7L17.5 9.5l-4.7 1.8L11 16l-1.8-4.7L4.5 9.5l4.7-1.8L11 3z" />
      <path d="M18 14v5M15.5 16.5h5" />
    </>
  ),
  ecommerce: (
    <>
      <path d="M6 8h12l-1 12H7L6 8z" />
      <path d="M9 8a3 3 0 016 0" />
    </>
  ),
  software: <path d="M8 8l-4 4 4 4M16 8l4 4-4 4M13.5 5l-3 14" />,
};

/** "e-commerce" no se parte en "e-" / "commerce" al llegar al borde de la linea. */
function keepEcommerceTogether(label: string): ReactNode[] {
  return label.split(/(e-commerce)/i).map((part, index) =>
    /^e-commerce$/i.test(part) ? (
      <span key={index} className="solution-link__nowrap">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

/**
 * Entrada a la landing de solucion desde una seccion de la home.
 *
 * Antes era un enlace de texto subrayado de 15px metido entre el parrafo y los
 * mockups: quedaba como una nota al pie, y en la seccion de IA -por el orden
 * flex de mobile- terminaba ARRIBA del titulo, solo y diminuto. Es la pagina que
 * mas queremos que se visite (recibe el trafico de busqueda por problema), asi
 * que ahora es una tarjeta con peso propio: icono, texto de dos lineas y una
 * flecha que se mueve al pasar el mouse.
 *
 * Toda la tarjeta es el enlace (area tactil grande). El kicker es decorativo:
 * el nombre accesible es el texto, que ya dice a donde lleva.
 */
function SolutionLink({ to, label, kicker, variant }: SolutionLinkProps) {
  return (
    <Link to={to} className={`solution-link solution-link--${variant}`}>
      <span className="solution-link__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" {...STROKE}>
          {ICONS[variant]}
        </svg>
      </span>

      <span className="solution-link__text">
        <span className="solution-link__kicker" aria-hidden="true">
          {kicker}
        </span>
        <span className="solution-link__label">{keepEcommerceTogether(label)}</span>
      </span>

      <span className="solution-link__arrow" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" {...STROKE} strokeWidth={2.5}>
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </span>
    </Link>
  );
}

export default SolutionLink;
